/**
 * Navigation Menu Slider
 * 
 * This script initializes and manages the slider functionality in the navigation menu.
 * It creates an interactive slider for blog/content items in the header navigation,
 * with automatic advancement, manual navigation controls, and accessibility features.
 * 
 * The slider uses the Slick Carousel library and includes:
 * - Automatic slide advancement with a 5-second timer
 * - Manual navigation with prev/next arrows
 * - Dot indicators for direct slide selection
 * - Fade transitions between slides
 * - Accessibility attributes for screen readers
 * 
 * @requires slick-carousel
 */

/**
 * Helper function to check if element has a class
 * 
 * @param {Element} element - The DOM element
 * @param {string} className - The class to check for
 * @returns {boolean} - Whether the element has the class
 */
function hasClass(element, className) {
  if (element && element.classList) {
    return element.classList.contains(className);
  } else if (element) {
    return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
  }
  return false;
}

let isNavSliderInitialized = false;

/**
 * Main function to initialize and configure the navigation menu slider
 */
function initializeNavSlider() {
  if (isNavSliderInitialized) {
    return;
  }

  // Initialize the slider
  var sliderElement = document.querySelector('.header__nav-top--content .content-slider');
  var slideTimer; // Variable to store the timeout for automatic advancement
  
  // Exit if no slider exists
  if (!sliderElement) return;
  
  /**
   * Initialize the Slick Carousel with configuration options
   * This creates the interactive slider with all necessary controls
   * 
   * Note: Slick is still jQuery-based, so we need to use jQuery syntax for initialization
   */
  if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick !== 'undefined') {
    // Get reference to navigation elements
    const prevArrow = document.querySelector('.header__nav-top--content .slider-prev');
    const nextArrow = document.querySelector('.header__nav-top--content .slider-next');
    const dotsContainer = document.querySelector('.header__nav-top--content .slider-dots');
    
    // Initialize slick using jQuery
    jQuery(sliderElement).slick({
      slidesToShow: 1,          // Show one slide at a time
      slidesToScroll: 1,        // Scroll one slide at a time
      arrows: true,             // Show navigation arrows
      dots: true,               // Show navigation dots
      infinite: true,           // Enable infinite looping
      speed: 500,               // Transition speed in milliseconds
      fade: true,               // Use fade transition effect
      cssEase: 'linear',        // CSS easing for animations
      autoplay: false,          // Disable built-in autoplay (we handle this manually)
      autoplaySpeed: 5000,      // 5 seconds between slides (used as reference)
      pauseOnHover: false,      // Don't pause on hover
      pauseOnFocus: false,      // Don't pause on focus
      prevArrow: jQuery(prevArrow),  // Custom previous arrow
      nextArrow: jQuery(nextArrow),  // Custom next arrow
      appendDots: jQuery(dotsContainer), // Custom dots container
      /**
       * Custom function to generate the HTML for each dot indicator
       * 
       * @param {Object} slider - The slider instance
       * @param {number} i - The index of the dot
       * @returns {string} HTML for the dot button with accessibility attributes
       */
      customPaging: function(slider, i) {
        return '<button type="button" aria-label="Go to slide ' + (i + 1) + '"></button>';
      }
    });
    
    /**
     * Event handler for slider initialization and slide changes
     * Updates accessibility attributes and restarts the progress animation
     */
    jQuery(sliderElement).on('init afterChange', function(event, slick, currentSlide) {
      // Set aria-hidden attributes for proper screen reader behavior
      const itemContents = document.querySelectorAll('.slider__item-content');
      itemContents.forEach(content => {
        content.setAttribute('aria-hidden', 'true');
      });
      
      const activeContents = document.querySelectorAll('.slider__item-content.slick-active');
      activeContents.forEach(content => {
        content.setAttribute('aria-hidden', 'false');
      });
      
      // Reset and start the progress animation
      startProgressAnimation();
    });
  }
  
  /**
   * Handles the automatic advancement of slides
   * Sets a timer to advance to the next slide after 5 seconds
   * This function is called on initialization and after each slide change
   */
  function startProgressAnimation() {
    // Clear any existing timer first to prevent multiple timers
    clearTimeout(slideTimer);
    
    // Wait for 5 seconds then advance to the next slide
    slideTimer = setTimeout(function() {
      if (typeof jQuery !== 'undefined' && sliderElement) {
        jQuery(sliderElement).slick('slickNext');
      }
    }, 5000);
  }
  
  /**
   * Event handler for manual navigation using prev/next arrows
   * Resets the timer when user manually navigates
   */
  const prevNextButtons = document.querySelectorAll('.header__nav-top--content .slider-prev, .header__nav-top--content .slider-next');
  prevNextButtons.forEach(button => {
    button.addEventListener('click', function() {
      // Clear the existing timer
      clearTimeout(slideTimer);
      
      // Start a new timer immediately
      startProgressAnimation();
    });
  });
  
  /**
   * Event handler for manual navigation using dot indicators
   * Resets the timer when user manually navigates
   */
  function setupDotButtonListeners() {
    const dotButtons = document.querySelectorAll('.header__nav-top--content .slider-dots button');
    dotButtons.forEach(button => {
      button.addEventListener('click', function() {
        // Clear the existing timer
        clearTimeout(slideTimer);
        
        // Start a new timer immediately
        startProgressAnimation();
      });
    });
  }
  
  // Need to wait for Slick to create the dots before we can add listeners
  setTimeout(setupDotButtonListeners, 100);
  
  // Start the progress animation on initial load
  startProgressAnimation();

  isNavSliderInitialized = true;
}

window.initializeNavSlider = initializeNavSlider;

document.addEventListener('DOMContentLoaded', function() {
  setupBlogHeaderSlider();
});

// ============================================================
// Blog Header Slider Behavior on Scroll
// ============================================================
// This section controls the behavior of the slider on blog pages when scrolling
// It only affects pages with the header--blog class

function setupBlogHeaderSlider() {
  // Only run this code on blog pages
  const header = document.getElementById('header');
  
  if (!header || !hasClass(header, 'header--blog')) {
    return; // Exit if not a blog page
  }
  
  // Get slider elements
  const slider = document.querySelector('.content-slider');
  let slideTimer; // Variable to store the timeout for automatic advancement
  
  // Skip if no slider exists
  if (!slider) {
    return;
  }
  
  // Function to pause slider
  function pauseSlider() {
    // Clear any existing timer
    if (typeof slideTimer !== 'undefined') {
      clearTimeout(slideTimer);
    }
    
    // If using Slick's built-in autoplay and jQuery is available
    if (typeof jQuery !== 'undefined' && hasClass(slider, 'slick-initialized')) {
      jQuery(slider).slick('slickPause');
    }
    
    // Also try to pause any other timers that might be running
    const otherSliders = document.querySelectorAll('.header__nav-top--content .content-slider');
    otherSliders.forEach(otherSlider => {
      if (typeof jQuery !== 'undefined' && hasClass(otherSlider, 'slick-initialized')) {
        jQuery(otherSlider).slick('slickPause');
      }
    });
  }
  
  // Function to handle scroll events
  function handleSliderOnScroll() {
    if (hasClass(header, 'header--is-scrolled')) {
      // Pause the slider when scrolled
      pauseSlider();
    }
  }
  
  // Add scroll event listener
  window.addEventListener('scroll', handleSliderOnScroll);
  
  // Also run on page load to ensure proper initial state
  handleSliderOnScroll();
  
  // Add a more aggressive check that runs every 100ms
  // This ensures the slider pauses even if scroll events are missed
  setInterval(function() {
    if (hasClass(header, 'header--is-scrolled')) {
      pauseSlider();
    }
  }, 100);
}